import { type FC, Suspense, lazy, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import { useLocationTab } from "src/hooks/UseLocationTab";
import { useConnectedSources } from "src/layouts/SourceConnector/Hooks/UseConnectedSources";
import { SOURCE_ID_TO_NAME_MAP } from "src/layouts/SourceConnector/Utils/SourceConstants";
import {
  PAID_MEDIA_TAB_I18N_KEYS,
  PAID_MEDIA_TAB_IDS,
  getVisiblePaidMediaTabs,
} from "./PaidMediaTabsDashboard.data";
import "./PaidMediaDashboard.scss";

const PaidMediaOverviewDashboard = lazy(
  () => import("../overview/PaidMediaOverviewDashboard")
);
const MetaAdsReport = lazy(() => import("./MetaAdsReport"));
const GoogleAdsReport = lazy(() => import("./GoogleAdsReport"));

const PaidMediaTabsDashboard: FC = () => {
  const { t } = useTranslation();
  const defaultEndDate = dayjs().format("YYYY-MM-DD");
  const defaultStartDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
  const { connectedSources, status } = useConnectedSources(SOURCE_ID_TO_NAME_MAP);
  const [requestedTab, setActiveTab] = useLocationTab(PAID_MEDIA_TAB_IDS, "overview");
  const visibleTabs =
    status === "error" ? PAID_MEDIA_TAB_IDS : getVisiblePaidMediaTabs(connectedSources);
  const activeTab = visibleTabs.includes(requestedTab) ? requestedTab : visibleTabs[0];
  const [appliedStartDate, setAppliedStartDate] = useState(defaultStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState(defaultEndDate);
  const [draftStartDate, setDraftStartDate] = useState(defaultStartDate);
  const [draftEndDate, setDraftEndDate] = useState(defaultEndDate);

  const dateFilters = useMemo(
    () => ({
      appliedStartDate,
      appliedEndDate,
      draftStartDate,
      draftEndDate,
      onApplyDates: ({
        startDate,
        endDate,
      }: {
        startDate: string;
        endDate: string;
      }) => {
        setDraftStartDate(startDate);
        setDraftEndDate(endDate);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
      },
    }),
    [appliedEndDate, appliedStartDate, draftEndDate, draftStartDate]
  );

  if (status === "loading") {
    return (
      <div className="paid-media-tabs-dashboard__loading" role="status" aria-live="polite">
        <CircularLoader size={32} thickness={4} />
      </div>
    );
  }

  if (visibleTabs.length === 0) {
    return (
      <div className="paid-media-tabs-dashboard__coming-soon">
        {t("metaAdsReport.paidMediaTabs.noConnectedSources")}
      </div>
    );
  }

  return (
    <div className="paid-media-tabs-dashboard">
      <div className="paid-media-tabs-dashboard__tabs">
        {visibleTabs.map(tabId => (
          <button
            key={tabId}
            type="button"
            className={`paid-media-tabs-dashboard__tab ${
              activeTab === tabId ? "paid-media-tabs-dashboard__tab--active" : ""
            }`}
            onClick={() => setActiveTab(tabId)}
          >
            {t(PAID_MEDIA_TAB_I18N_KEYS[tabId])}
          </button>
        ))}
      </div>

      <div className="paid-media-dashboard">
        <Suspense
          fallback={
            <div className="paid-media-tabs-dashboard__loading" role="status" aria-live="polite">
              <CircularLoader size={32} thickness={4} />
            </div>
          }
        >
          {activeTab === "overview" ? <PaidMediaOverviewDashboard /> : null}
          {activeTab === "meta-ads-report" ? <MetaAdsReport dateFilters={dateFilters} /> : null}
          {activeTab === "google-ads-report" ? (
            <GoogleAdsReport dateFilters={dateFilters} />
          ) : null}
          {activeTab === "tiktok-ads-report" ? (
            <div className="paid-media-tabs-dashboard__coming-soon">
              {t("metaAdsReport.paidMediaTabs.comingSoon")}
            </div>
          ) : null}
        </Suspense>
      </div>
    </div>
  );
};

export default PaidMediaTabsDashboard;
